import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { User, Save, Lock, Globe, Palette, FileText, Shield, ShieldCheck } from 'lucide-react';

function Section({ title, icon: Icon, children }) {
  return <div className="card p-6 mb-6"><h2 className="font-bold text-pn-dark mb-4 flex items-center gap-2"><Icon size={18} className="text-pn-red" />{title}</h2>{children}</div>;
}

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const lang = user?.language || 'it';
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAPass, setTwoFAPass] = useState('');
  const [toggling2FA, setToggling2FA] = useState(false);
  const [twoFAGlobal, setTwoFAGlobal] = useState(true);

  useEffect(() => { (async () => {
    try { const { data } = await api.get('/users/profile'); setForm(data.user); } catch (e) {}
    try { const { data } = await api.get('/settings/public'); setTwoFAGlobal(data.two_factor_global !== 'false'); } catch (e) {}
    finally { setLoading(false); }
  })(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    try { const { data } = await api.put('/users/profile', form); updateUser(data.user); toast.success('Profilo aggiornato!'); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (pwForm.new_password !== pwForm.confirm) return toast.error('Le password non corrispondono');
    if (pwForm.new_password.length < 8) return toast.error('Minimo 8 caratteri');
    try { await api.put('/users/password', { current_password: pwForm.current_password, new_password: pwForm.new_password }); toast.success('Password aggiornata!'); setPwForm({ current_password: '', new_password: '', confirm: '' }); setShowPw(false); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  const toggle2FA = async () => {
    if (!twoFAPass) return toast.error('Inserisci la password per confermare');
    setToggling2FA(true);
    try {
      const newState = !form.two_factor_enabled;
      const { data } = await api.post('/auth/toggle-2fa', { enable: newState, password: twoFAPass });
      setForm(p => ({ ...p, two_factor_enabled: newState ? 1 : 0 }));
      toast.success(data.message);
      setShow2FA(false);
      setTwoFAPass('');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setToggling2FA(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pn-red" /></div>;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-pn-dark mb-6">{lang === 'it' ? 'Il Mio Profilo' : 'My Profile'}</h1>

      <Section title={lang === 'it' ? 'Dati Personali' : 'Personal Info'} icon={User}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="block text-xs font-medium mb-1">Nome</label><input className="input-field" value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">Cognome</label><input className="input-field" value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} /></div>
          <div className="sm:col-span-2"><label className="block text-xs font-medium mb-1">Email</label><input className="input-field bg-gray-50" value={form.email || ''} disabled /></div>
          <div className="sm:col-span-2"><label className="block text-xs font-medium mb-1">Indirizzo</label><input className="input-field" value={form.address_street || ''} onChange={e => set('address_street', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">CAP</label><input className="input-field" value={form.address_zip || ''} onChange={e => set('address_zip', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">Città</label><input className="input-field" value={form.address_city || ''} onChange={e => set('address_city', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">Provincia</label><input className="input-field" value={form.address_province || ''} onChange={e => set('address_province', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">Nazione</label><input className="input-field" value={form.address_country || ''} onChange={e => set('address_country', e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Dati Fiscali" icon={User}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="block text-xs font-medium mb-1">Codice Fiscale</label><input className="input-field" value={form.fiscal_code || ''} onChange={e => set('fiscal_code', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">P.IVA</label><input className="input-field" value={form.vat_number || ''} onChange={e => set('vat_number', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">Codice SDI</label><input className="input-field" value={form.sdi_code || ''} onChange={e => set('sdi_code', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">PEC</label><input className="input-field" value={form.pec_email || ''} onChange={e => set('pec_email', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">IBAN</label><input className="input-field" value={form.iban || ''} onChange={e => set('iban', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">Intestatario IBAN</label><input className="input-field" value={form.iban_holder || ''} onChange={e => set('iban_holder', e.target.value)} /></div>
          <div><label className="block text-xs font-medium mb-1">Metodo Pagamento</label><select className="input-field" value={form.payment_method || 'bank_transfer'} onChange={e => set('payment_method', e.target.value)}><option value="bank_transfer">Bonifico Bancario</option><option value="paypal">PayPal</option></select></div>
          {form.payment_method === 'paypal' && <div><label className="block text-xs font-medium mb-1">Email PayPal</label><input className="input-field" value={form.paypal_email || ''} onChange={e => set('paypal_email', e.target.value)} /></div>}
        </div>
      </Section>

      <Section title="Social Links" icon={Globe}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="block text-xs font-medium mb-1">Instagram</label><input className="input-field" value={form.link_instagram || ''} onChange={e => set('link_instagram', e.target.value)} placeholder="https://instagram.com/..." /></div>
          <div><label className="block text-xs font-medium mb-1">TikTok</label><input className="input-field" value={form.link_tiktok || ''} onChange={e => set('link_tiktok', e.target.value)} placeholder="https://tiktok.com/@..." /></div>
          <div><label className="block text-xs font-medium mb-1">YouTube</label><input className="input-field" value={form.link_youtube || ''} onChange={e => set('link_youtube', e.target.value)} placeholder="https://youtube.com/..." /></div>
          <div><label className="block text-xs font-medium mb-1">Facebook</label><input className="input-field" value={form.link_facebook || ''} onChange={e => set('link_facebook', e.target.value)} placeholder="https://facebook.com/..." /></div>
          <div className="sm:col-span-2"><label className="block text-xs font-medium mb-1">Sito Web</label><input className="input-field" value={form.link_website || ''} onChange={e => set('link_website', e.target.value)} placeholder="https://..." /></div>
        </div>
      </Section>

      <Section title="Preferenze" icon={Palette}>
        <div><label className="block text-xs font-medium mb-1">Lingua</label><select className="input-field w-48" value={form.language || 'it'} onChange={e => set('language', e.target.value)}><option value="it">Italiano</option><option value="en">English</option></select></div>
      </Section>

      <div className="flex gap-3 mb-6">
        <button onClick={saveProfile} disabled={saving} className="btn-primary inline-flex items-center gap-2"><Save size={16} />{saving ? '...' : 'Salva Profilo'}</button>
        <button onClick={() => setShowPw(!showPw)} className="btn-secondary inline-flex items-center gap-2"><Lock size={16} />Cambia Password</button>
      </div>

      {showPw && (
        <div className="card p-6 mb-6">
          <h2 className="font-bold mb-4">Cambia Password</h2>
          <div className="space-y-4 max-w-md">
            <div><label className="block text-xs font-medium mb-1">Password Attuale</label><input type="password" className="input-field" value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} /></div>
            <div><label className="block text-xs font-medium mb-1">Nuova Password</label><input type="password" className="input-field" value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} placeholder="Min. 8 caratteri" /></div>
            <div><label className="block text-xs font-medium mb-1">Conferma</label><input type="password" className="input-field" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} /></div>
            <button onClick={changePw} className="btn-primary">Aggiorna Password</button>
          </div>
        </div>
      )}

      {/* Sicurezza - 2FA (solo se abilitato globalmente) */}
      {twoFAGlobal && (
      <Section title="Sicurezza" icon={ShieldCheck}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">{lang === 'it' ? 'Verifica in due passaggi' : 'Two-factor authentication'}</p>
            <p className="text-xs text-gray-400 mt-1">{lang === 'it' ? 'Ricevi un codice via email ad ogni login per maggiore sicurezza' : 'Receive a code via email on each login for extra security'}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-medium ${form.two_factor_enabled ? 'text-green-600' : 'text-gray-400'}`}>
              {form.two_factor_enabled ? (lang === 'it' ? 'Attiva' : 'Active') : (lang === 'it' ? 'Disattivata' : 'Disabled')}
            </span>
            <button onClick={() => setShow2FA(true)}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.two_factor_enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.two_factor_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        {show2FA && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
            <p className="text-sm mb-3">{form.two_factor_enabled
              ? (lang === 'it' ? 'Inserisci la password per disattivare la verifica in due passaggi:' : 'Enter password to disable 2FA:')
              : (lang === 'it' ? 'Inserisci la password per attivare la verifica in due passaggi:' : 'Enter password to enable 2FA:')}</p>
            <div className="flex gap-2 max-w-sm">
              <input type="password" className="input-field flex-1" value={twoFAPass} onChange={e => setTwoFAPass(e.target.value)} placeholder="La tua password"
                onKeyDown={e => { if (e.key === 'Enter') toggle2FA(); }} />
              <button onClick={toggle2FA} disabled={toggling2FA} className="btn-primary px-4">
                {toggling2FA ? '...' : 'Conferma'}
              </button>
              <button onClick={() => { setShow2FA(false); setTwoFAPass(''); }} className="btn-secondary px-3">X</button>
            </div>
          </div>
        )}
      </Section>
      )}

      {/* Documenti Legali */}
      <div className="card p-6 mb-6">
        <h2 className="font-bold text-pn-dark mb-4 flex items-center gap-2"><FileText size={18} className="text-pn-red" />Documenti Legali</h2>
        <div className="space-y-2">
          <Link to="/legal/terms" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition text-sm text-gray-700 border border-gray-100">
            <Shield size={16} className="text-pn-red" />
            <span className="flex-1">Termini e Condizioni</span>
            {form.tc_accepted_at && <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Accettati</span>}
          </Link>
          <Link to="/legal/dmca" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition text-sm text-gray-700 border border-gray-100">
            <FileText size={16} className="text-gray-400" />
            <span>DMCA / Copyright Policy</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
